import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PackageCheck,
  Link as LinkIcon,
  History,
  ChevronDown,
  Package,
  Truck,
  Search,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { fetchEpcOrders } from "../../store/slices/orderSlice";
import PaginationControls from "../../utils/PaginationControls";

interface EpcOrdersTabProps {
  searchTerm?: string;
  dateRange?: { from: string; to: string };
}

const EpcOrdersTab: React.FC<EpcOrdersTabProps> = ({
  searchTerm,
  dateRange,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const { epcOrders, paginations, loading } = useSelector(
    (state: RootState) => state.order,
  );

  const [page, setPage] = useState(1);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  const [localSearch, setLocalSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [fromDate, setFromDate] = useState(dateRange?.from || "");
  const [toDate, setToDate] = useState(dateRange?.to || "");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(localSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [localSearch]);

  useEffect(() => {
    dispatch(
      fetchEpcOrders({
        page,
        search: debouncedSearch || searchTerm,
        startDate: fromDate || undefined,
        endDate: toDate || undefined,
      }),
    );
  }, [dispatch, page, debouncedSearch, searchTerm, fromDate, toDate]);

  const toggleExpand = (orderId: string) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const pagination = paginations.epcOrders;

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-orange-100 text-orange-700";
      case "partiallyDispatched":
        return "bg-purple-100 text-purple-700";
      case "cancelled":
        return "bg-red-100 text-red-700";
      default:
        return "bg-blue-100 text-blue-700";
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between px-1 gap-3">
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <PackageCheck size={16} className="text-indigo-600" />
          Order History ({pagination?.total || 0})
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search orders..."
              value={localSearch}
              onChange={(e) => {
                setLocalSearch(e.target.value);
                setPage(1);
              }}
              className="pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 w-full md:w-48 bg-white"
            />
          </div>

          <div className="flex items-center gap-1 border border-slate-200 rounded-lg px-2 py-1 bg-white">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="text-xs text-slate-600 focus:outline-none bg-transparent py-0.5"
            />
            <span className="text-slate-400 text-xs">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="text-xs text-slate-600 focus:outline-none bg-transparent py-0.5"
            />
          </div>
        </div>
      </div>

      {loading && epcOrders.length === 0 ? (
        <div className="flex justify-center py-10 text-slate-400 text-xs animate-pulse">
          Loading orders...
        </div>
      ) : epcOrders.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-xs italic border-2 border-dashed border-slate-100 rounded-xl">
          No EPC orders found.
        </div>
      ) : (
        <div className="space-y-2">
          {epcOrders.map((order) => {
            const isExpanded = expandedOrderId === order._id;
            const lastTracking =
              order.trackingDetails?.[order.trackingDetails.length - 1];

            return (
              <div
                key={order._id}
                className={`border rounded-xl transition-all overflow-hidden ${isExpanded ? "border-indigo-200 shadow-sm" : "border-slate-100 hover:border-slate-200"}`}
              >
                <div
                  onClick={() => toggleExpand(order._id)}
                  className="flex items-center justify-between p-3 cursor-pointer bg-white hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-50 rounded-lg text-slate-400">
                      <Package size={16} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-800">
                        #{order.orderNumber}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {new Date(
                          order.updatedAt || order.createdDate,
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span
                      className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${getStatusStyle(order.dispatch ?? "")}`}
                    >
                      {order.dispatch || "Assigning Warehouse"}
                    </span>
                    <motion.div animate={{ rotate: isExpanded ? 180 : 0 }}>
                      <ChevronDown size={14} className="text-slate-400" />
                    </motion.div>
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      className="bg-slate-50/50 border-t border-slate-100"
                    >
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                          <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <Package size={12} /> Items Progress
                          </h4>
                          <div className="space-y-1.5">
                            {order.goods?.map((item: any, idx: number) => (
                              <div
                                key={idx}
                                className="bg-white p-2 rounded-lg border border-slate-200 flex justify-between items-center text-[11px]"
                              >
                                <span className="font-medium truncate max-w-[150px]">
                                  {item.product?.productName ||
                                    item.rawMaterial?.name}
                                </span>
                                <div className="text-right">
                                  <span className="font-bold text-slate-700">
                                    {item.dispatchedQty || 0} / {item.quantity}
                                  </span>
                                  <span className="text-slate-400 ml-1">
                                    {item.unit}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                              <Truck size={12} /> Shipping
                            </h4>
                            {lastTracking ? (
                              <div className="bg-white p-2 rounded-lg border border-slate-200 text-[11px] space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Mode:</span>
                                  <span className="font-bold">
                                    {lastTracking.transportMode || "N/A"}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">No:</span>
                                  <span className="font-mono text-indigo-600">
                                    {lastTracking.trackingNumber || "N/A"}
                                  </span>
                                </div>
                                {lastTracking.trackingLink && (
                                  <a
                                    href={lastTracking.trackingLink}
                                    target="_blank"
                                    className="flex items-center gap-1 text-indigo-500 font-bold hover:underline pt-1"
                                  >
                                    <LinkIcon size={10} /> Track Package
                                  </a>
                                )}
                              </div>
                            ) : (
                              <div className="text-[10px] text-slate-400 italic">
                                No tracking info yet
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                              <History size={12} /> Dispatch Batches
                            </h4>
                            <div className="max-h-32 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                              {order.assignmentHistory?.map(
                                (batch: any, bIdx: number) => (
                                  <div
                                    key={bIdx}
                                    className="bg-white/50 p-2 rounded-lg border border-dashed border-slate-200 text-[10px]"
                                  >
                                    <div className="font-bold text-slate-600 mb-1">
                                      Batch #{bIdx + 1} -{" "}
                                      {new Date(
                                        batch.assignedAt,
                                      ).toLocaleDateString()}
                                    </div>
                                    {batch.items?.map(
                                      (bi: any, biIdx: number) => (
                                        <div
                                          key={biIdx}
                                          className="flex justify-between text-slate-500 italic"
                                        >
                                          <span>{bi.itemName}</span>
                                          <span>
                                            {bi.quantity} {bi.unit}
                                          </span>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      {pagination && pagination.pages > 1 && (
        <div className="pt-2">
          <PaginationControls
            currentPage={page}
            totalPages={pagination.pages}
            onPageChange={setPage}
            isLoading={loading}
            totalItems={pagination.total}
          />
        </div>
      )}
    </section>
  );
};

export default EpcOrdersTab;
